package org.gymly.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.gymly.data.PickedMediaHolder
import org.gymly.data.PortalRepo
import org.gymly.model.ClientPaymentMethod
import org.gymly.model.PickedImagePayload
import org.gymly.model.PortalMembershipOptions
import org.gymly.ui.components.ProofPreview
import org.gymly.model.PortalMembershipOrder
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.material3.HorizontalDivider
import org.gymly.model.PortalPayment
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.OutlinedButton
import org.gymly.model.PortalPaymentStatus
import org.gymly.model.PortalProofRules
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.MethodChipGrid
import org.gymly.ui.components.clientMethodIcon
import org.gymly.ui.components.MobilePageHeader
import org.gymly.ui.components.ProofAttachmentView
import org.gymly.ui.components.TwoButtonRow
import org.gymly.util.formatMoney
import org.gymly.util.todayIsoDate
import org.gymly.util.openProofFilePicker
import org.gymly.util.userMessage

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PaymentsScreen(
    mobile: Boolean = false,
    unreadCount: Long = 0,
    langEs: Boolean = true,
    onLangEs: (Boolean) -> Unit = {},
    onBell: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var rows by remember { mutableStateOf<List<PortalPayment>>(emptyList()) }
    var orders by remember { mutableStateOf<List<PortalMembershipOrder>>(emptyList()) }
    var ordersShown by remember { mutableStateOf(MEMBERSHIP_ORDERS_STEP) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var formOpen by remember { mutableStateOf(false) }
    var detailItem by remember { mutableStateOf<PortalPayment?>(null) }
    // Pago de membresía: plan y entrenador; el monto lo pone el plan según el método (Bs o $).
    var membershipOptions by remember { mutableStateOf<PortalMembershipOptions?>(null) }
    var planId by remember { mutableStateOf<String?>(null) }
    var trainerId by remember { mutableStateOf<String?>(null) }
    var method by remember { mutableStateOf(ClientPaymentMethod.MOBILE_PAYMENT) }
    var notes by remember { mutableStateOf("") }
    var paymentDate by remember { mutableStateOf(todayIsoDate()) }
    var proofName by remember { mutableStateOf<String?>(null) }
    val proofHolder = remember { PickedMediaHolder() }
    var saving by remember { mutableStateOf(false) }
    val uriHandler = androidx.compose.ui.platform.LocalUriHandler.current
    // Cobro de membresía con tarjeta o PayPal: se abrió su página de pago; falta confirmar al volver.
    var gatewayCheckout by remember { mutableStateOf<org.gymly.model.PortalMembershipCheckout?>(null) }
    // Aviso de referencia repetida (misma fecha de pago): solo informa, el pago se puede enviar igual.
    var referenceWarning by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(formOpen, method, notes, paymentDate) {
        referenceWarning = null
        val ref = notes.trim()
        if (!formOpen || !method.requiresProof() || ref.length < 5) return@LaunchedEffect
        delay(500)
        val duplicate = runCatching { PortalRepo.api.checkPaymentReference(ref, paymentDate.trim()) }.getOrDefault(false)
        referenceWarning = if (duplicate) {
            "Esta referencia ya está registrada en un pago de esa fecha. Puedes enviarlo igual; el gimnasio lo revisará."
        } else {
            null
        }
    }

    fun clearProof() {
        proofHolder.clear()
        proofName = null
    }

    fun pickProof() {
        openProofFilePicker { picked ->
            if (picked == null) {
                proofHolder.clear()
                scope.launch { error = "No se seleccionó ningún archivo." }
                return@openProofFilePicker
            }
            val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
            proofHolder.set(payload, picked.fileName)
            scope.launch {
                proofName = picked.fileName
                error = null
            }
        }
    }

    fun load() {
        scope.launch {
            error = null
            try {
                rows = PortalRepo.api.fetchPayments()
                // Al volver de Stripe o PayPal, confirma los cobros con pasarela que siguen pendientes.
                var pagados = 0
                rows.filter { it.method.isGateway() && it.status == PortalPaymentStatus.PENDING }.forEach { cobro ->
                    if (runCatching { PortalRepo.api.confirmMembershipCheckout(cobro.id) }.getOrDefault(false)) pagados++
                }
                if (pagados > 0) rows = PortalRepo.api.fetchPayments()
                orders = runCatching { PortalRepo.api.fetchMembershipOrders() }.getOrDefault(emptyList())
                membershipOptions = runCatching { PortalRepo.api.fetchMembershipOptions() }.getOrNull()
            } catch (e: Exception) {
                error = e.userMessage("Error al cargar pagos.")
            } finally {
                loading = false
            }
        }
    }

    fun openCreate() {
        planId = null
        trainerId = null
        method = ClientPaymentMethod.MOBILE_PAYMENT
        notes = ""
        paymentDate = todayIsoDate()
        clearProof()
        formOpen = true
    }

    fun save() {
        scope.launch {
            if (planId == null) {
                error = "Elige el plan que pagas."
                return@launch
            }
            if (trainerId == null) {
                error = "Elige el entrenador."
                return@launch
            }
            if (method.isGateway()) {
                // Tarjeta o PayPal: el servidor arma el monto con el plan en $ y devuelve la página de pago.
                saving = true
                error = null
                try {
                    val inicio = PortalRepo.api.startMembershipCheckout(planId!!, trainerId!!, method, notes)
                    formOpen = false
                    clearProof()
                    gatewayCheckout = inicio
                    uriHandler.openUri(inicio.url)
                    load()
                } catch (e: Exception) {
                    error = e.userMessage("No se pudo iniciar el pago.")
                } finally {
                    saving = false
                }
                return@launch
            }
            if (method.requiresProof() && proofHolder.payload == null) {
                error = "Adjunta el comprobante (imagen o PDF)."
                return@launch
            }
            if (!isReferenceValid(method.name, notes.trim())) {
                error = "La referencia de pago móvil debe tener 5 dígitos (los últimos del comprobante)."
                return@launch
            }
            // Fecha de pago (yyyy-MM-dd): la fecha en que ocurrió el pago, elegida por el socio.
            val fecha = paymentDate.trim()
            if (!Regex("\\d{4}-\\d{2}-\\d{2}").matches(fecha)) {
                error = "Indica una fecha de pago válida (aaaa-mm-dd)."
                return@launch
            }
            saving = true
            error = null
            try {
                val orden = PortalRepo.api.createMembershipPayment(
                    planId!!,
                    trainerId!!,
                    method,
                    notes,
                    paymentDate = fecha,
                )
                val proof = proofHolder.payload
                val cobroId = orden.paymentId
                if (proof != null && cobroId != null) {
                    val withProof = PortalRepo.api.uploadPaymentProof(cobroId, proof)
                    if (withProof.proofUrl.isNullOrBlank()) {
                        error = "El pago se guardó pero el comprobante no. Edítalo e inténtalo de nuevo."
                        load()
                        return@launch
                    }
                }
                formOpen = false
                clearProof()
                load()
            } catch (e: Exception) {
                error = e.userMessage("No se pudo guardar el pago.")
            } finally {
                saving = false
            }
        }
    }

    LaunchedEffect(Unit) {
        runCatching { PortalProofRules.rules = PortalRepo.api.fetchPaymentProofRules() }
        load()
    }

    gatewayCheckout?.let { checkout ->
        AlertDialog(
            onDismissRequest = { if (!saving) gatewayCheckout = null },
            title = { Text("Completa el pago", fontWeight = androidx.compose.ui.text.font.FontWeight.Bold) },
            text = {
                Column {
                    Text(
                        "Se abrió la página de pago de la membresía. Cuando termines, vuelve aquí y pulsa " +
                            "Verificar pago.",
                    )
                    error?.let {
                        Text(it, color = GymlyColors.Error, fontSize = 13.sp, modifier = Modifier.padding(top = 8.dp))
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            val pagado = runCatching { PortalRepo.api.confirmMembershipCheckout(checkout.paymentId) }
                                .getOrDefault(false)
                            if (pagado) {
                                gatewayCheckout = null
                                load()
                            } else {
                                error = "Todavía no vemos el pago. Si ya pagaste, espera un momento y vuelve a verificar."
                            }
                            saving = false
                        }
                    },
                    enabled = !saving,
                ) {
                    Text("Verificar pago")
                }
            },
            dismissButton = {
                TextButton(onClick = { gatewayCheckout = null }, enabled = !saving) { Text("Más tarde") }
            },
        )
    }

    if (loading && rows.isEmpty()) {
        GymlyLoadingCenter()
        return
    }

    val subtitle =
        "Registra pagos (pago móvil, transferencia, Zelle, USDT). Quedan pendientes hasta que el gimnasio los confirme."

    BoxWithConstraints(Modifier.fillMaxSize().background(GymlyColors.Surface)) {
        val compact = mobile || maxWidth < 700.dp

        Column(Modifier.fillMaxSize()) {
            if (mobile) {
                MobilePageHeader(
                    title = "Pagos",
                    subtitle = subtitle,
                    unreadCount = unreadCount,
                    langEs = langEs,
                    onLangEs = onLangEs,
                    onBell = onBell,
                )
            }

            Column(
                Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(GymlySpace.lg),
            ) {
                // Membresía vigente de cortesía: no se registran pagos aquí (la tienda sí). El historial sigue visible.
                // Vigente = la orden más reciente es de cortesía y su vencimiento (yyyy-MM-dd) aún no pasó.
                val ultima = orders.firstOrNull()
                val cortesia = ultima?.status == "COURTESY" &&
                    (ultima.endsAt.isNullOrBlank() || ultima.endsAt >= todayIsoDate())
                if (!mobile) {
                    if (compact) {
                        Text("Pagos", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                        Spacer(Modifier.height(6.dp))
                        Text(subtitle, color = GymlyColors.TextMuted, fontSize = 14.sp)
                        Spacer(Modifier.height(12.dp))
                        Button(
                            onClick = { openCreate() },
                            enabled = !cortesia,
                            colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                            shape = RoundedCornerShape(10.dp),
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Text("Registrar pago", fontWeight = FontWeight.SemiBold)
                        }
                    } else {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.Top,
                        ) {
                            Column(Modifier.weight(1f).padding(end = 16.dp)) {
                                Text("Pagos", fontWeight = FontWeight.Bold, fontSize = 28.sp, color = GymlyColors.Text)
                                Spacer(Modifier.height(6.dp))
                                Text(subtitle, color = GymlyColors.TextMuted, fontSize = 14.sp)
                            }
                            Button(
                                onClick = { openCreate() },
                            enabled = !cortesia,
                                colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                                shape = RoundedCornerShape(10.dp),
                            ) {
                                Text("Registrar pago", fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }
                } else {
                    Button(
                        onClick = { openCreate() },
                            enabled = !cortesia,
                        colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text("Registrar pago", fontWeight = FontWeight.SemiBold)
                    }
                }

                Spacer(Modifier.height(16.dp))
                error?.let {
                    Text(it, color = GymlyColors.Error, modifier = Modifier.padding(bottom = 10.dp))
                }
                if (cortesia) {
                    Text(
                        "Tu membresía es de cortesía, por eso no puedes registrar pagos aquí. Podrás hacerlo cuando tengas una membresía pagada.",
                        color = GymlyColors.TextMuted,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(bottom = 10.dp),
                    )
                }

                // Mis pagos (arriba): los pagos validados aparecen aquí. Los que siguen por validar van abajo.
                Text("Mis pagos", fontWeight = FontWeight.Bold, fontSize = 22.sp, color = GymlyColors.Text)
                Spacer(Modifier.height(10.dp))
                if (orders.isEmpty()) {
                    Text("No hay órdenes de membresía.", color = GymlyColors.TextMuted)
                } else {
                    orders.take(ordersShown).forEach { MembershipOrderRow(it) }
                    if (orders.size > ordersShown) {
                        OutlinedButton(
                            onClick = { ordersShown += MEMBERSHIP_ORDERS_STEP },
                            modifier = Modifier.fillMaxWidth().heightIn(min = 48.dp),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text("Ver más", fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
                Spacer(Modifier.height(20.dp))
                MembershipSummary(orders, compact)
                Spacer(Modifier.height(20.dp))
                Text("Pagos por validar", fontWeight = FontWeight.Bold, fontSize = 22.sp, color = GymlyColors.Text)
                Spacer(Modifier.height(10.dp))
                val porValidar = rows.filter { it.status == PortalPaymentStatus.POR_VALIDAR }
                if (porValidar.isEmpty()) {
                    Text("No hay pagos por validar.", color = GymlyColors.TextMuted)
                } else {
                    porValidar.forEach { item ->
                        PaymentCard(
                            item = item,
                            mobile = mobile,
                            compact = compact,
                            onView = { detailItem = item },
                        )
                        Spacer(Modifier.height(10.dp))
                    }
                }
                Spacer(Modifier.height(GymlySpace.xl))
            }
        }
    }

    if (formOpen) {
        if (mobile) {
            val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
            ModalBottomSheet(
                onDismissRequest = { if (!saving) formOpen = false },
                sheetState = sheetState,
                containerColor = GymlyColors.Surface,
            ) {
                PaymentMobileSheetContent(
                    options = membershipOptions,
                    planId = planId,
                    onPlanId = { planId = it },
                    trainerId = trainerId,
                    onTrainerId = { trainerId = it },
                    method = method,
                    onMethod = { method = it },
                    notes = notes,
                    onNotes = { notes = it },
                    paymentDate = paymentDate,
                    onPaymentDate = { paymentDate = it },
                    referenceWarning = referenceWarning,
                    proofName = proofName,
                    onPickProof = { pickProof() },
                    proofPayload = proofHolder.payload,
                    onRemoveProof = { clearProof() },
                    saving = saving,
                    onDismiss = { if (!saving) { formOpen = false; clearProof() } },
                    onSave = { save() },
                )
            }
        } else {
            PaymentFormDialog(
                options = membershipOptions,
                planId = planId,
                onPlanId = { planId = it },
                trainerId = trainerId,
                onTrainerId = { trainerId = it },
                method = method,
                onMethod = { method = it },
                notes = notes,
                onNotes = { notes = it },
                paymentDate = paymentDate,
                onPaymentDate = { paymentDate = it },
                referenceWarning = referenceWarning,
                proofName = proofName,
                onPickProof = { pickProof() },
                proofPayload = proofHolder.payload,
                onRemoveProof = { clearProof() },
                saving = saving,
                onDismiss = { if (!saving) { formOpen = false; clearProof() } },
                onSave = { save() },
            )
        }
    }

    detailItem?.let { item ->
        AlertDialog(
            onDismissRequest = { detailItem = null },
            title = { Text("Detalle del pago", fontWeight = FontWeight.Bold) },
            text = {
                Column(Modifier.widthIn(max = 420.dp).verticalScroll(rememberScrollState())) {
                    Text(
                        formatMoney(item.amountCents, item.currency),
                        fontWeight = FontWeight.Bold,
                        fontSize = 20.sp,
                        color = GymlyColors.Text,
                    )
                    Spacer(Modifier.height(8.dp))
                    Text("${item.methodLabel} · ${item.statusLabel}", color = GymlyColors.TextMuted, fontSize = 14.sp)
                    if (item.paymentDate.isNotBlank()) {
                        Spacer(Modifier.height(4.dp))
                        Text("Fecha: ${item.paymentDate}", color = GymlyColors.TextMuted, fontSize = 13.sp)
                    }
                    if (!item.notes.isNullOrBlank()) {
                        Spacer(Modifier.height(8.dp))
                        Text(item.notes, color = GymlyColors.Text, fontSize = 13.sp)
                    }
                    if (!item.proofUrl.isNullOrBlank()) {
                        Spacer(Modifier.height(14.dp))
                        ProofAttachmentView(
                            proofUrl = item.proofUrl,
                            proofContentType = item.proofContentType,
                            label = "Comprobante",
                            accent = GymlyColors.Primary,
                            muted = GymlyColors.TextMuted,
                            surface = GymlyColors.Surface,
                            text = GymlyColors.Text,
                            outline = GymlyColors.Outline,
                        )
                    } else {
                        Spacer(Modifier.height(10.dp))
                        Text("Sin comprobante adjunto.", color = GymlyColors.TextMuted, fontSize = 13.sp)
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { detailItem = null }) { Text("Cerrar") }
            },
        )
    }
}

@Composable
private fun PaymentCard(
    item: PortalPayment,
    mobile: Boolean,
    compact: Boolean,
    onView: () -> Unit,
) {
    val statusColor = when (item.status) {
        PortalPaymentStatus.PENDING -> Color(0xFFEF6C00)
        PortalPaymentStatus.PAID -> Color(0xFF2E7D32)
        PortalPaymentStatus.FAILED -> GymlyColors.Error
        PortalPaymentStatus.REFUNDED -> GymlyColors.TextMuted
        PortalPaymentStatus.POR_VALIDAR -> Color(0xFF1565C0)
    }
    val statusBg = when (item.status) {
        PortalPaymentStatus.PENDING -> Color(0xFFFFF3E0)
        PortalPaymentStatus.PAID -> Color(0xFFE8F5E9)
        PortalPaymentStatus.FAILED -> GymlyColors.ErrorContainer
        PortalPaymentStatus.REFUNDED -> GymlyColors.Outline.copy(alpha = 0.25f)
        PortalPaymentStatus.POR_VALIDAR -> Color(0xFFE3F2FD)
    }

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(14.dp))
            .clickable(onClick = onView)
            .padding(horizontal = 18.dp, vertical = 16.dp),
    ) {
        if (mobile) {
            Text(
                formatMoney(item.amountCents, item.currency),
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = GymlyColors.Text,
            )
            Spacer(Modifier.height(4.dp))
            Text(
                "${item.methodLabel} · ${item.statusLabel}",
                color = GymlyColors.TextMuted,
                fontSize = 13.sp,
            )
            if (!item.proofUrl.isNullOrBlank()) {
                Spacer(Modifier.height(4.dp))
                Text(
                    "Ver comprobante",
                    color = GymlyColors.Primary,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
            if (!item.notes.isNullOrBlank()) {
                Spacer(Modifier.height(4.dp))
                Text(item.notes, color = GymlyColors.TextMuted, fontSize = 12.sp)
            }
        } else {
            val subtitle = listOfNotNull(
                item.methodLabel,
                item.paymentDate.takeIf { it.isNotBlank() },
            ).joinToString(" · ")
            if (compact) {
                Text(
                    formatMoney(item.amountCents, item.currency),
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = GymlyColors.Text,
                )
                Spacer(Modifier.height(4.dp))
                Text(subtitle, color = GymlyColors.TextMuted, fontSize = 13.sp)
                if (!item.proofUrl.isNullOrBlank()) {
                    Spacer(Modifier.height(4.dp))
                    Text(
                        "Ver comprobante",
                        color = GymlyColors.Primary,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
                Spacer(Modifier.height(10.dp))
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    StatusBadge(item.statusLabel, statusColor, statusBg)
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f).padding(end = 12.dp)) {
                        Text(
                            formatMoney(item.amountCents, item.currency),
                            fontWeight = FontWeight.Bold,
                            fontSize = 18.sp,
                            color = GymlyColors.Text,
                        )
                        Spacer(Modifier.height(4.dp))
                        Text(subtitle, color = GymlyColors.TextMuted, fontSize = 13.sp)
                        if (!item.proofUrl.isNullOrBlank()) {
                            Spacer(Modifier.height(4.dp))
                            Text(
                                "Ver comprobante",
                                color = GymlyColors.Primary,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }
                    StatusBadge(item.statusLabel, statusColor, statusBg)
                }
            }
        }
    }
}

@Composable
private fun StatusBadge(label: String, color: Color, background: Color) {
    Text(
        label,
        color = color,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(background)
            .padding(horizontal = 10.dp, vertical = 5.dp),
    )
}

@Composable
private fun PaymentMobileSheetContent(
    options: PortalMembershipOptions?,
    planId: String?,
    onPlanId: (String) -> Unit,
    trainerId: String?,
    onTrainerId: (String) -> Unit,
    method: ClientPaymentMethod,
    onMethod: (ClientPaymentMethod) -> Unit,
    notes: String,
    onNotes: (String) -> Unit,
    paymentDate: String,
    onPaymentDate: (String) -> Unit,
    referenceWarning: String? = null,
    proofName: String?,
    onPickProof: () -> Unit,
    proofPayload: org.gymly.model.PickedImagePayload? = null,
    onRemoveProof: () -> Unit = {},
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .padding(horizontal = GymlySpace.lg)
            .padding(bottom = GymlySpace.xl),
    ) {
        Text("Nuevo pago", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(16.dp))
        MembershipPlanTrainerFields(
            options = options,
            planId = planId,
            onPlanId = onPlanId,
            trainerId = trainerId,
            onTrainerId = onTrainerId,
            method = method,
        )
        Spacer(Modifier.height(14.dp))
        Text("Método", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(8.dp))
        MethodChipGrid(
            choices = membershipMethodChoices(options, planId),
            selected = method,
            label = { it.labelEs() },
            icon = ::clientMethodIcon,
            onSelect = onMethod,
        )
        method.hint()?.let { hint ->
            Spacer(Modifier.height(8.dp))
            Text(hint, color = GymlyColors.TextMuted, fontSize = 12.sp)
        }
        Spacer(Modifier.height(14.dp))
        Text("Fecha de pago", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
            Spacer(Modifier.height(6.dp))
            OutlinedTextField(
                value = paymentDate,
                onValueChange = onPaymentDate,
                modifier = Modifier.fillMaxWidth(),
                placeholder = { Text("aaaa-mm-dd") },
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = GymlyColors.Primary,
                    unfocusedBorderColor = GymlyColors.Outline,
                ),
            )
        if (method.requiresProof()) {
            Spacer(Modifier.height(14.dp))
            Text("Referencia / nota", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
            Spacer(Modifier.height(6.dp))
            OutlinedTextField(
                value = notes,
                onValueChange = { onNotes(referenceInputFor(method.name, it)) },
                modifier = Modifier.fillMaxWidth(),
                placeholder = { Text("Nº referencia, teléfono, red…") },
                minLines = 2,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = GymlyColors.Primary,
                    unfocusedBorderColor = GymlyColors.Outline,
                ),
            )
            referenceWarning?.let {
                Spacer(Modifier.height(6.dp))
                Text(it, color = Color(0xFFEF6C00), fontSize = 12.sp)
            }
            Spacer(Modifier.height(14.dp))
            Text("Comprobante (imagen o PDF)", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
            Spacer(Modifier.height(6.dp))
            ProofPreview(
                payload = proofPayload,
                fileName = proofName,
                enabled = !saving,
                onPick = onPickProof,
                onRemove = onRemoveProof,
            )
        }
        Spacer(Modifier.height(18.dp))
        TwoButtonRow(
            cancelText = "Cancelar",
            confirmText = "Guardar",
            onCancel = onDismiss,
            onConfirm = onSave,
            loading = saving,
        )
    }
}

@Composable
private fun PaymentFormDialog(
    options: PortalMembershipOptions?,
    planId: String?,
    onPlanId: (String) -> Unit,
    trainerId: String?,
    onTrainerId: (String) -> Unit,
    method: ClientPaymentMethod,
    onMethod: (ClientPaymentMethod) -> Unit,
    notes: String,
    onNotes: (String) -> Unit,
    paymentDate: String,
    onPaymentDate: (String) -> Unit,
    referenceWarning: String? = null,
    proofName: String?,
    onPickProof: () -> Unit,
    proofPayload: org.gymly.model.PickedImagePayload? = null,
    onRemoveProof: () -> Unit = {},
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text("Nuevo pago", fontWeight = FontWeight.Bold)
        },
        text = {
            Column(Modifier.widthIn(max = 420.dp).verticalScroll(rememberScrollState())) {
                MembershipPlanTrainerFields(
                    options = options,
                    planId = planId,
                    onPlanId = onPlanId,
                    trainerId = trainerId,
                    onTrainerId = onTrainerId,
                    method = method,
                )
                Spacer(Modifier.height(14.dp))
                Text("Método", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
                Spacer(Modifier.height(6.dp))
                MethodChipGrid(
                    choices = membershipMethodChoices(options, planId),
                    selected = method,
                    label = { it.labelEs() },
                    icon = ::clientMethodIcon,
                    onSelect = onMethod,
                )
                method.hint()?.let { hint ->
                    Spacer(Modifier.height(8.dp))
                    Text(hint, color = GymlyColors.TextMuted, fontSize = 12.sp)
                }
                Spacer(Modifier.height(14.dp))
                Text("Fecha de pago", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(6.dp))
                    OutlinedTextField(
                        value = paymentDate,
                        onValueChange = onPaymentDate,
                        modifier = Modifier.fillMaxWidth(),
                        placeholder = { Text("aaaa-mm-dd") },
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = GymlyColors.Primary,
                            unfocusedBorderColor = GymlyColors.Outline,
                        ),
                    )
                if (method.requiresProof()) {
                    Spacer(Modifier.height(14.dp))
                    Text("Referencia / nota", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(6.dp))
                    OutlinedTextField(
                        value = notes,
                        onValueChange = { onNotes(referenceInputFor(method.name, it)) },
                        modifier = Modifier.fillMaxWidth(),
                        placeholder = { Text("Nº referencia, teléfono, red…") },
                        minLines = 2,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = GymlyColors.Primary,
                            unfocusedBorderColor = GymlyColors.Outline,
                        ),
                    )
                    referenceWarning?.let {
                        Spacer(Modifier.height(6.dp))
                        Text(it, color = Color(0xFFEF6C00), fontSize = 12.sp)
                    }
                    Spacer(Modifier.height(14.dp))
                    Text("Comprobante (imagen o PDF)", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = GymlyColors.Text)
                    Spacer(Modifier.height(6.dp))
                    ProofPreview(
                        payload = proofPayload,
                        fileName = proofName,
                        enabled = !saving,
                        onPick = onPickProof,
                        onRemove = onRemoveProof,
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = onSave,
                enabled = !saving,
                colors = ButtonDefaults.buttonColors(containerColor = GymlyColors.Primary),
                shape = RoundedCornerShape(10.dp),
            ) {
                if (saving) {
                    CircularProgressIndicator(Modifier.size(18.dp), color = Color.White, strokeWidth = 2.dp)
                } else {
                    Text("Guardar")
                }
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !saving) {
                Text("Cancelar")
            }
        },
    )
}

private const val MEMBERSHIP_ORDERS_STEP = 10

private fun membershipStatusColor(status: String): Color = when (status) {
    "PAID" -> Color(0xFF2E7D32)
    "PENDING" -> Color(0xFFEF6C00)
    "COURTESY" -> Color(0xFF7C3AED)
    "POR_VALIDAR" -> Color(0xFF1565C0)
    else -> GymlyColors.TextMuted
}

/** Arriba: entrenadores y planes a la izquierda, gráfico de torta de estados a la derecha. */
@Composable
private fun MembershipSummary(orders: List<PortalMembershipOrder>, compact: Boolean) {
    if (orders.isEmpty()) return
    val trainers = orders.mapNotNull { it.trainerName }.groupingBy { it }.eachCount()
        .entries.sortedByDescending { it.value }.take(5)
    val plans = orders.groupingBy { it.planName }.eachCount()
        .entries.sortedByDescending { it.value }.take(5)
    if (compact) {
        TrainersAndPlansCard(trainers, plans, Modifier.fillMaxWidth())
        Spacer(Modifier.height(12.dp))
        StatusBarCard(orders, Modifier.fillMaxWidth())
    } else {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.Top,
        ) {
            TrainersAndPlansCard(trainers, plans, Modifier.weight(1f))
            StatusBarCard(orders, Modifier.weight(1f))
        }
    }
    Spacer(Modifier.height(20.dp))
}

private val MEMBERSHIP_PALETTE = listOf(
    Color(0xFF3F51B5),
    Color(0xFF7C3AED),
    Color(0xFF0E7C86),
    Color(0xFFEF6C00),
    Color(0xFF2E7D32),
)

/** Panel con fondo, borde y título; el contenido va separado por líneas. */
@Composable
private fun SummaryPanel(title: String, modifier: Modifier, content: @Composable ColumnScope.() -> Unit) {
    Column(
        modifier
            .clip(RoundedCornerShape(16.dp))
            .background(GymlyColors.SurfaceContainer)
            .border(1.dp, GymlyColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(16.dp))
            .padding(16.dp),
    ) {
        Text(title, fontWeight = FontWeight.Bold, fontSize = 15.sp, color = GymlyColors.Text)
        Spacer(Modifier.height(8.dp))
        content()
    }
}

/** Barra horizontal proporcional: pista de fondo y relleno de color. */
@Composable
private fun BarTrack(fraction: Float, color: Color) {
    Box(
        Modifier
            .fillMaxWidth()
            .height(8.dp)
            .clip(RoundedCornerShape(4.dp))
            .background(GymlyColors.Outline.copy(alpha = 0.2f)),
    ) {
        Box(
            Modifier
                .fillMaxWidth(fraction.coerceIn(0f, 1f))
                .height(8.dp)
                .clip(RoundedCornerShape(4.dp))
                .background(color),
        )
    }
}

@Composable
private fun TrainersAndPlansCard(
    trainers: List<Map.Entry<String, Int>>,
    plans: List<Map.Entry<String, Int>>,
    modifier: Modifier,
) {
    SummaryPanel("Entrenadores contratados", modifier) {
        if (trainers.isEmpty()) {
            Text("Sin entrenador asignado.", color = GymlyColors.TextMuted, fontSize = 13.sp)
        }
        trainers.forEachIndexed { i, entry ->
            if (i > 0) HorizontalDivider(color = GymlyColors.Outline.copy(alpha = 0.35f))
            Row(
                Modifier.fillMaxWidth().padding(vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    Modifier
                        .size(34.dp)
                        .clip(CircleShape)
                        .background(MEMBERSHIP_PALETTE[i % MEMBERSHIP_PALETTE.size]),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(entry.key.take(1).uppercase(), color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                }
                Spacer(Modifier.width(10.dp))
                Text(
                    entry.key,
                    Modifier.weight(1f),
                    color = GymlyColors.Text,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 14.sp,
                    maxLines = 1,
                )
                Text(
                    "${entry.value}",
                    color = MEMBERSHIP_PALETTE[i % MEMBERSHIP_PALETTE.size],
                    fontWeight = FontWeight.Bold,
                    fontSize = 14.sp,
                )
            }
        }
        Spacer(Modifier.height(12.dp))
        Text("Planes más tomados", fontWeight = FontWeight.SemiBold, fontSize = 14.sp, color = GymlyColors.Text)
        val maxPlan = plans.maxOfOrNull { it.value }?.coerceAtLeast(1) ?: 1
        plans.forEachIndexed { i, entry ->
            Column(Modifier.fillMaxWidth().padding(vertical = 6.dp)) {
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Text(entry.key, Modifier.weight(1f), color = GymlyColors.Text, fontSize = 13.sp, maxLines = 1)
                    Text("${entry.value}", color = GymlyColors.TextMuted, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                }
                Spacer(Modifier.height(5.dp))
                BarTrack(entry.value.toFloat() / maxPlan, MEMBERSHIP_PALETTE[i % MEMBERSHIP_PALETTE.size])
            }
        }
    }
}

@Composable
private fun StatusBarCard(orders: List<PortalMembershipOrder>, modifier: Modifier) {
    val groups = orders.groupBy { it.status }.map { (status, list) ->
        Triple(status, list.first().statusLabel, list.size)
    }
    val maxCount = groups.maxOfOrNull { it.third }?.coerceAtLeast(1) ?: 1
    SummaryPanel("Estado de tus órdenes", modifier) {
        groups.forEachIndexed { i, (status, label, n) ->
            if (i > 0) HorizontalDivider(color = GymlyColors.Outline.copy(alpha = 0.35f))
            Column(Modifier.fillMaxWidth().padding(vertical = 8.dp)) {
                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Text(label, Modifier.weight(1f), color = membershipStatusColor(status), fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                    Text("$n", color = membershipStatusColor(status), fontWeight = FontWeight.Bold, fontSize = 14.sp)
                }
                Spacer(Modifier.height(6.dp))
                BarTrack(n.toFloat() / maxCount, membershipStatusColor(status))
            }
        }
    }
}

@Composable
private fun MembershipOrderRow(o: PortalMembershipOrder) {
    Column(
        Modifier
            .fillMaxWidth()
            .padding(bottom = 8.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(GymlyColors.SurfaceContainer)
            .padding(horizontal = 14.dp, vertical = 12.dp),
    ) {
        Text("#${o.orderNumber} · ${o.planName}", fontWeight = FontWeight.SemiBold, color = GymlyColors.Text, maxLines = 1)
        Spacer(Modifier.height(2.dp))
        Text(
            "${o.trainerName ?: "Sin entrenador"} · Inicio ${o.orderDate} · Vencimiento ${o.endsAt ?: "—"}",
            color = GymlyColors.TextMuted,
            fontSize = 13.sp,
        )
        Spacer(Modifier.height(4.dp))
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(formatMoney(o.amountCents.toInt(), o.currency), fontWeight = FontWeight.SemiBold, color = GymlyColors.Text)
            Text(o.statusLabel, fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = membershipStatusColor(o.status))
        }
        o.payments.forEach { line ->
            Spacer(Modifier.height(3.dp))
            Text(line, color = GymlyColors.TextMuted, fontSize = 12.sp)
        }
    }
}
